/**
 * InkMatrix.tsx
 * 「能量场矩阵」—— AI 创作助手首页（模式选择屏）的视觉主体，会话开始后停靠到面板顶部中央。
 *
 * 形态：5 圈同心环（1 → 6 → 13 → 19 → 25 → 31 = 95 点）铺在 96px 见方的方框里，
 *   点径与静态透明度随环距径向衰减（中心大而实、边缘小而淡）⇒ 读作一个由小圆组成的圆盘。
 *   几何与随机场参数都在 `inkMatrixField.ts` 里算完（纯函数、固定种子、模块加载时一次），
 *   本文件只负责渲染。其中 MATRIX_SIZE 必须与 ai-panel.css 的
 *   --ink-matrix-size / --ink-matrix-half 同源，改一处必须同时改另一处。
 *
 * 动效（2026-10 改版：**同心波 → 随机场**）：
 *   旧版 95 个点跑同一条 3s 呼吸曲线、相位按环分配（同环完全一致）⇒ 一圈圈规律向外推，
 *   太死板。现在每个点挂两个**独立**动画：尺寸层（transform）与亮度层（opacity），
 *   相位 / 周期 / 幅度逐点随机，且取自二维值噪声（空间连续）⇒ 成片亮斑随时间流动变形，
 *   另有 4 个"脉冲点"偶尔闪一下 —— 一片活着的能量场。
 *
 * 性能：随机性在模块加载时烘焙进内联 CSS 变量，动画全部由 CSS 承担（只动 transform /
 *   opacity，compositor 合成）—— **运行时零 JS 计算、无 rAF**。每帧用 JS 改 95 个元素的
 *   style 会在写作时抢主线程，装饰动画不该付这个代价。
 *   忙时（AI 生成中）换一组幅度更大、周期更短的关键帧，并扫出一道细弧。
 *
 * 位置：矩阵自身不决定摆在哪 —— 外层 .ai-rw-matrix-layer（inset:0）由容器的
 *   --home / --conv / --conv.is-started 修饰类给出 translate，用一条 CSS transition
 *   完成「面板中央 → 会话头下方」的位移（同一 DOM 节点，全程不卸载、尺寸不变）。
 */
import React from 'react';
import { buildInkMatrixDots } from './inkMatrixField';

/** 随机场参数在模块加载时算一次（纯函数 + 固定种子 ⇒ 视觉可复现、可单测） */
const DOTS = buildInkMatrixDots();

interface InkMatrixProps {
  /** AI 生成中：幅度更大、节奏更快，并在圆盘外沿扫出一道细弧（挂 .is-busy，参数见 ai-panel.css） */
  busy?: boolean;
}

export function InkMatrix({ busy = false }: InkMatrixProps) {
  return (
    <div className={`ai-ink-matrix${busy ? ' is-busy' : ''}`} aria-hidden="true">
      {DOTS.map((d, i) => (
        <span
          key={i}
          className={
            'ai-ink-matrix-block'
            + (d.core ? ' ai-ink-matrix-block--core' : '')
            + (d.blip ? ' ai-ink-matrix-block--blip' : '')
          }
          style={{
            left: d.left,
            top: d.top,
            width: d.size,
            height: d.size,
            // 静态径向透明度走**颜色 alpha**（color-mix），把 opacity 整个让给动画
            ['--ink-dot-alpha']: `${d.alphaPct}%`,
            ['--ink-amp-a']: d.ampA,
            ['--ink-dur-a']: d.durA,
            ['--ink-d-a']: d.delayA,
            ['--ink-amp-b']: d.ampB,
            ['--ink-dur-b']: d.durB,
            ['--ink-d-b']: d.delayB,
          } as React.CSSProperties}
        />
      ))}
      {/* 工作态标记：圆盘外沿一道 1px 细弧（常态 display:none；.is-busy 时缓速扫过一圈）。
          纯装饰，只动 transform: rotate() 与 border-color，无发光/无渐变。 */}
      <span className="ai-ink-matrix-arc" />
    </div>
  );
}

export default InkMatrix;
